import { ModuleWrapper } from '@/components'
import { Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { Field } from '@/modules/companies'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import styled from 'styled-components'
import { ALL_DRIVER_FORECASTS_QUERY } from '../queries'

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

interface RemainingDrivingTimeProps {}

export const RemainingDrivingTime: React.FC<RemainingDrivingTimeProps> = () => {
  const translations = useTranslate({
    remainingDriving: 'common.remaining-driving-time',
    minutes: 'common.minutes',
    search: 'common.search',
    user: 'common.user',
    dailyRemainingDriving: 'activities.daily-remaining-driving',
    weeklyRemainingDriving: 'activities.weekly-remaining-driving',
    twoWeeklyRemainingDriving: 'activities.two-weekly-remaining-driving',
  })

  const { hasPermissions } = usePermissions()
  const me = useUser()

  const canSeeOthers = hasPermissions(
    PERMISSIONS.activities.change.driveractivity,
    true
  )

  const { formValues: filter, formChangeHandler: handler } = useForm({
    values: {
      search: '',
    },
  })

  const { data, loading } = useQuery(ALL_DRIVER_FORECASTS_QUERY)

  const userForecasts = (() => {
    if (!data?.allForecasts) return []

    const filteredForecasts = data.allForecasts.filter(forecast => {
      const user = forecast.user
      if (!user) return false

      if (!user.fullName.toLowerCase().includes(filter.search.toLowerCase()))
        return false

      if (canSeeOthers) return true
      if (forecast.user?.id === me?.id) return true

      return false
    })

    return filteredForecasts
  })()

  return (
    <ModuleWrapper title={translations.remainingDriving} loading={loading}>
      <Controls>
        <section>
          <Field>
            <label>{translations.user}</label>
            <Input
              value={filter.search}
              onChange={handler('search')}
              grayed
              placeholder={translations.search}
            />
          </Field>
        </section>
      </Controls>

      {userForecasts.length > 0 &&
        userForecasts.map(forecast => {
          const user = forecast.user

          const minutesDaily = forecast.remainingDaily ?? 0
          const dailyDisplay =
            minutesDaily <= 60
              ? `${minutesDaily} ` + translations.minutes
              : `${Math.round(minutesDaily / 60)}:${(minutesDaily % 60)
                  .toString()
                  .padStart(2, '0')}`

          const minutesWeekly = forecast.remainingWeekly ?? 0
          const weeklyDisplay =
            minutesWeekly <= 60
              ? `${minutesWeekly} ` + translations.minutes
              : `${Math.round(minutesWeekly / 60)}:${(minutesWeekly % 60)
                  .toString()
                  .padStart(2, '0')}`

          const minutesTwoWeekly = forecast.remainingTwoWeekly ?? 0
          const twoWeeklyDisplay =
            minutesTwoWeekly <= 60
              ? `${minutesTwoWeekly} ` + translations.minutes
              : `${Math.round(minutesTwoWeekly / 60)}:${(minutesTwoWeekly % 60)
                  .toString()
                  .padStart(2, '0')}`

          return (
            <Card key={forecast.timestamp} $margin="0 0 1rem 0">
              <h2>{user?.fullName}</h2>

              <p>
                {translations.dailyRemainingDriving}: <b>{dailyDisplay}</b>
              </p>

              <p>
                {translations.weeklyRemainingDriving}: <b>{weeklyDisplay}</b>
              </p>

              <p>
                {translations.twoWeeklyRemainingDriving}:{' '}
                <b>{twoWeeklyDisplay}</b>
              </p>
            </Card>
          )
        })}
    </ModuleWrapper>
  )
}
